import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

interface FaqItem {
  id: string;
  tag: string;
  q: string;
  a: string;
  link?: { label: string; href: string };
}

const faqs: FaqItem[] = [
  {
    id: 'do-you-cover-my-area',
    tag: 'Coverage',
    q: 'Do you only work with businesses in Far North Queensland?',
    a: 'No — while we\'re based in Mission Beach and have deep roots across Far North Queensland, Cairns, and the Cassowary Coast, we work with businesses all across Australia entirely remotely. Sydney, Melbourne, Brisbane, Perth, Adelaide — we\'ve delivered results in every major market. Location genuinely does not affect outcomes when the strategy and execution are strong.',
  },
  {
    id: 'small-regional-town',
    tag: 'Regional',
    q: 'We\'re in a small regional town. Is it worth doing SEO?',
    a: 'Often more worth it than in the city — and faster. Regional markets typically have less competition online, which means a well-structured local SEO campaign can reach position one within weeks rather than months. The businesses that establish that dominance early tend to hold it for years because competitors rarely catch up. If your town has residents or visitors searching for what you do, there\'s a real opportunity sitting untouched.',
    link: { label: 'See how we work in regional areas', href: '/service-areas#cassowary-coast' },
  },
  {
    id: 'in-person-visits',
    tag: 'Process',
    q: 'Do you need to visit us in person to get started?',
    a: 'Never. Our entire process — strategy, design, SEO, GEO, and ongoing optimisation — runs fully remotely from day one. We use video calls for strategy sessions, shared document workflows for content, and async reporting so you can review results in your own time. We\'ve onboarded clients in Darwin, Perth, and Auckland without a single in-person meeting and delivered the same standard of work.',
  },
  {
    id: 'why-qld-agency',
    tag: 'Credibility',
    q: 'We\'re in Melbourne. Why would we use a QLD-based agency?',
    a: 'Because results are results regardless of postcode. Our clients in Melbourne and Sydney aren\'t paying for proximity — they\'re paying for an AI-first SEO strategy that actually moves their rankings. We\'ve ranked businesses in competitive southern markets against agencies three times our size. Our edge is technical: AI search optimisation, clean schema architecture, and GEO programmes that most metro agencies still haven\'t figured out.',
    link: { label: 'See our GEO / AEO programme', href: '/geo-aeo' },
  },
  {
    id: 'local-vs-national',
    tag: 'Strategy',
    q: 'What\'s the difference between local SEO and national SEO?',
    a: 'Local SEO targets searches tied to a specific location — "web designer Cairns" or "accountant Mission Beach" — and focuses on Google Business Profile, map pack rankings, and geo-targeted content. National SEO targets broader keyword categories across all of Australia without a geographic anchor. Many of our clients run both simultaneously: local to dominate their immediate market and capture walk-in and referral traffic, national for service-based enquiries that have no geographic dependency.',
  },
  {
    id: 'google-business-multiple-locations',
    tag: 'Local SEO',
    q: 'Can you manage Google Business Profiles for multiple locations?',
    a: 'Yes. Multi-location businesses are something we handle frequently — separate optimised Google Business Profiles for each location, consistent NAP data across directories, location-specific landing pages with unique content, and individual review strategies per branch. Everything is managed centrally with location-specific reporting so you can see how each site is performing independently.',
  },
  {
    id: 'tourism-regional-advantage',
    tag: 'Tourism',
    q: 'We run a tourism business in a regional area. Is there a specific strategy for that?',
    a: 'Absolutely — and it\'s different to standard local SEO. Tourism search is seasonal, mobile-heavy, and heavily influenced by aggregator platforms like TripAdvisor and Expedia. We build tourism SEO strategies that capture direct bookings, reduce aggregator dependency, and include GEO optimisation so your business gets cited when travellers ask AI tools for recommendations. Our tourism clients are primarily across Far North Queensland — reef trips, ferry services, dive operators — so this is territory we know well.',
    link: { label: 'Tourism website design services', href: '/tourism-website-design' },
  },
  {
    id: 'ai-search-regional',
    tag: 'AI Search',
    q: 'Does GEO / AEO work for regional businesses, not just big brands?',
    a: 'Yes — and regional businesses actually have an early-mover advantage right now. Most small and regional operators have zero GEO presence, which means the field is open. AI engines don\'t favour size; they favour authority, clarity, and structured answers. A regional tourism operator or trades business with a well-executed GEO programme can outrank major brands for specific regional queries because no one else is competing for those AI citations yet.',
    link: { label: 'Learn about GEO / AEO', href: '/geo-aeo' },
  },
];

export default function ServiceAreasFaqSection() {
  const [openId, setOpenId] = useState<string>('do-you-cover-my-area');
  const groupRef = useScrollRevealGroup();

  const mid = Math.ceil(faqs.length / 2);
  const leftCol = faqs.slice(0, mid);
  const rightCol = faqs.slice(mid);

  return (
    <section id="service-areas-faq" className="relative pt-24 pb-16 bg-ds-bg overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-red/40 to-transparent" />
      <div className="absolute inset-0 hero-grid opacity-10 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[500px] bg-ds-red/4 blur-[150px] rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6" ref={groupRef}>

        {/* Header */}
        <div className="mb-16 reveal">
          <span className="section-label block mb-4">Service Area Questions</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-display text-6xl md:text-7xl text-white leading-none">
              COVERAGE.
              <br />
              <span className="text-ds-red neon-text-red">ANSWERED.</span>
            </h2>
            <p className="font-body text-white/45 text-sm leading-relaxed max-w-xs lg:text-right">
              The questions we get asked about who we work with and where.
              <br />
              <span className="text-white/70">Still unsure? </span>
              <a
                href="mailto:hello@darksidedigital.com.au"
                className="text-ds-red hover:text-white transition-colors duration-200"
              >
                Just ask.
              </a>
            </p>
          </div>
          <div className="mt-8 h-px w-24 bg-ds-red" style={{ boxShadow: '0 0 12px rgba(180,255,57,0.5)' }} />
        </div>

        {/* Two-column accordion */}
        <div className="grid lg:grid-cols-2 gap-4 reveal">

          {/* Left column */}
          <div className="space-y-2">
            {leftCol.map((faq) => {
              const isOpen = openId === faq.id;
              return (
                <FaqRow
                  key={faq.id}
                  faq={faq}
                  isOpen={isOpen}
                  onToggle={() => setOpenId(isOpen ? '' : faq.id)}
                />
              );
            })}
          </div>

          {/* Right column */}
          <div className="space-y-2">
            {rightCol.map((faq) => {
              const isOpen = openId === faq.id;
              return (
                <FaqRow
                  key={faq.id}
                  faq={faq}
                  isOpen={isOpen}
                  onToggle={() => setOpenId(isOpen ? '' : faq.id)}
                />
              );
            })}
          </div>
        </div>

        {/* CTA strip */}
        <div
          className="mt-16 border border-ds-border bg-ds-bg-2 p-10 flex flex-col md:flex-row items-start justify-between gap-8 reveal"
          style={{ borderLeft: '3px solid #B4FF39' }}
        >
          <div>
            <h3 className="font-display text-2xl text-white mb-1 tracking-wide">
              NOT SURE IF WE COVER YOUR AREA?
            </h3>
            <p className="font-body text-white/45 text-sm leading-relaxed">
              Call us or drop a message — we\'ll give you a straight answer and a free audit if we can help.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-shrink-0">
            <a
              href="tel:1300029054"
              className="btn-outline text-xs flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-phone-line" />
              1300 029 054
            </a>
            <Link
              to="/#contact"
              className="btn-red text-xs whitespace-nowrap inline-flex items-center gap-2"
            >
              <span>Get a Free Audit</span>
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}

interface FaqRowProps {
  faq: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
}

function FaqRow({ faq, isOpen, onToggle }: FaqRowProps) {
  return (
    <div
      className={`border transition-all duration-300 cursor-pointer ${
        isOpen
          ? 'border-ds-red/40 bg-ds-bg-2'
          : 'border-ds-border bg-ds-bg hover:border-white/20'
      }`}
      style={isOpen ? { boxShadow: '0 0 30px rgba(180,255,57,0.06)' } : {}}
      onClick={onToggle}
    >
      <div className="flex items-center justify-between gap-4 px-6 py-5">
        <div className="flex items-center gap-4 min-w-0">
          <span
            className={`font-body text-xs tracking-widest uppercase px-2 py-0.5 border flex-shrink-0 transition-all duration-300 ${
              isOpen
                ? 'border-ds-red/50 text-ds-red bg-ds-red/8'
                : 'border-ds-border text-white/30'
            }`}
          >
            {faq.tag}
          </span>
          <h4
            className={`font-heading font-700 text-sm leading-snug transition-colors duration-300 ${
              isOpen ? 'text-white' : 'text-white/75'
            }`}
          >
            {faq.q}
          </h4>
        </div>
        <div
          className={`w-7 h-7 flex items-center justify-center border flex-shrink-0 transition-all duration-300 ${
            isOpen
              ? 'border-ds-red/50 bg-ds-red/10 rotate-45'
              : 'border-ds-border rotate-0'
          }`}
        >
          <i
            className={`ri-add-line text-sm transition-colors duration-300 ${
              isOpen ? 'text-ds-red' : 'text-white/40'
            }`}
          />
        </div>
      </div>

      <div
        className={`overflow-hidden transition-all duration-400 ease-in-out ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 pb-6 pt-0">
          <div className="border-t border-ds-border/50 pt-4 space-y-3">
            <p className="font-body text-white/60 text-sm leading-relaxed">
              {faq.a}
            </p>
            {faq.link && (
              <Link
                to={faq.link.href}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 font-body text-xs text-ds-red hover:text-white transition-colors duration-200 group"
              >
                <span>{faq.link.label}</span>
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs group-hover:translate-x-0.5 transition-transform duration-150" />
                </div>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
